import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import KitchenQRCode from './KitchenQRCode';
import TestPaymentModal from './TestPaymentModal';

const RestaurantProfile = ({ apiClient, restaurant, onUpdate, onPurchaseCapacity }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTestPayment, setShowTestPayment] = useState(false);
  const [stripeConfig, setStripeConfig] = useState({
    stripe_account_id: '',
    stripe_publishable_key: '',
    stripe_secret_key: ''
  });
  const [editingStripe, setEditingStripe] = useState(false);
  const [currencies, setCurrencies] = useState({});
  const [hasProfilePassword, setHasProfilePassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [passwordAction, setPasswordAction] = useState(''); // 'profile' or 'stripe'
  const [pendingFormData, setPendingFormData] = useState(null);
  const [pendingStripeData, setPendingStripeData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    currency: 'USD',
    payment_mode: 'manual_only'
  });

  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || '',
        phone: restaurant.phone || '',
        address: restaurant.address || '',
        currency: restaurant.currency || 'USD',
        payment_mode: restaurant.payment_mode || 'manual_only'
      });
    }
    checkProfilePasswordStatus();
  }, [restaurant]);

  const checkProfilePasswordStatus = async () => {
    try {
      const response = await apiClient.get('/restaurant/profile/password-status');
      setHasProfilePassword(response.data.has_profile_password);
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };

  const handleSendPasswordSetup = async () => {
    try {
      const response = await apiClient.post('/restaurant/profile/send-password-setup');
      toast.success('Password setup email sent! Please check your email.');
      setShowPasswordSetup(true);
    } catch (error) {
      toast.error('Failed to send password setup email');
    }
  };

  const handlePasswordSubmit = async (password) => {
    try {
      await apiClient.post('/restaurant/profile/verify-password', { password });
      
      if (passwordAction === 'profile' && pendingFormData) {
        await submitProfileWithPassword(password);
      } else if (passwordAction === 'stripe' && pendingStripeData) {
        await submitStripeWithPassword(password);
      }
      
      setShowPasswordModal(false);
      setPendingFormData(null);
      setPendingStripeData(null);
      setPasswordAction('');
    } catch (error) {
      toast.error('Incorrect profile password');
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchStripeConfig();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await apiClient.get('/currencies');
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const response = await apiClient.get('/restaurant/stripe-config');
      setStripeConfig({
        stripe_account_id: response.data.stripe_account_id || '',
        stripe_publishable_key: response.data.stripe_publishable_key || '',
        stripe_secret_key: response.data.stripe_secret_key || ''
      });
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate restaurant name length
    if (formData.name && formData.name.length > 25) {
      toast.error('Restaurant name must be 25 characters or less');
      return;
    }

    // Enforce profile password requirement for all accounts
    if (!hasProfilePassword) {
      toast.error('Please set your profile password before saving profile changes');
      setEditing(false); // Exit editing mode
      return;
    }

    // If profile password is set, require authentication
    if (hasProfilePassword) {
      setPendingFormData(formData);
      setPasswordAction('profile');
      setShowPasswordModal(true);
      return;
    }

    await submitProfileWithPassword();
  };

  const submitProfileWithPassword = async (password = null) => {
    setLoading(true);

    try {
      // Update profile (name, phone, address, currency)
      const profileData = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        currency: formData.currency
      };

      // Add profile password if provided
      if (password) {
        profileData.profile_password = password;
      }

      const response = await apiClient.put('/restaurant/profile', profileData);

      // Update payment mode separately if it changed
      if (formData.payment_mode !== restaurant.payment_mode) {
        await apiClient.put('/restaurant/payment-mode', {
          payment_mode: formData.payment_mode
        });
      }

      toast.success('Profile updated successfully!');
      
      // Show currency change notification if currency was changed
      if (response.data.currency_changed) {
        toast.warning(response.data.currency_notification, {
          duration: 6000
        });
      }
      
      setEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: restaurant.name || '',
      phone: restaurant.phone || '',
      address: restaurant.address || '',
      currency: restaurant.currency || 'USD',
      payment_mode: restaurant.payment_mode || 'manual_only'
    });
    setEditing(false);
  };

  const hasStripeConfig = () => {
    return stripeConfig.stripe_publishable_key && stripeConfig.stripe_secret_key;
  };

  const handleStripeConfigChange = (e) => {
    setStripeConfig({
      ...stripeConfig,
      [e.target.name]: e.target.value
    });
  };

  const handleStripeConfigSubmit = async (e) => {
    e.preventDefault();
    
    // Enforce profile password requirement for all accounts
    if (!hasProfilePassword) {
      toast.error('Please set your profile password before saving Stripe configuration');
      setEditingStripe(false); // Exit editing mode
      return;
    }

    // If profile password is set, require authentication
    if (hasProfilePassword) {
      setPendingStripeData(stripeConfig);
      setPasswordAction('stripe');
      setShowPasswordModal(true);
      return;
    }

    await submitStripeWithPassword();
  };

  const submitStripeWithPassword = async (password = null) => {
    try {
      const configData = { ...stripeConfig };
      
      // Add profile password if provided
      if (password) {
        configData.profile_password = password;
      }

      await apiClient.post('/restaurant/stripe-config', configData);
      toast.success('Stripe configuration updated successfully!');
      setEditingStripe(false);
      fetchStripeConfig();
    } catch (error) {
      console.error('Error updating Stripe config:', error);
      toast.error('Failed to update Stripe configuration');
    }
  };

  return (
    <div className="card overflow-hidden">
      {/* Zero Capacity Alert Banner */}
      {restaurant?.order_capacity === 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="text-red-800 font-bold text-lg">Order Capacity Depleted!</h3>
                <p className="text-red-700 text-sm mt-1">
                  You have <strong>0 orders remaining</strong>. Customers cannot place new orders until you purchase more capacity.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (onPurchaseCapacity) {
                  onPurchaseCapacity();
                }
              }}
              className="btn-primary whitespace-nowrap px-6 py-3 text-base font-semibold"
            >
              💳 Purchase Capacity
            </button>
          </div>
        </div>
      )}

      {/* Low Capacity Warning Banner */}
      {restaurant?.order_capacity > 0 && restaurant?.order_capacity <= 50 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="text-yellow-800 font-bold text-lg">Order Capacity is Low!</h3>
                <p className="text-yellow-700 text-sm mt-1">
                  You have <strong>{restaurant?.order_capacity} orders remaining</strong>. Please purchase more capacity to ensure uninterrupted service.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (onPurchaseCapacity) {
                  onPurchaseCapacity();
                }
              }}
              className="btn-primary whitespace-nowrap px-6 py-3 text-base font-semibold"
            >
              💳 Purchase Capacity
            </button>
          </div>
        </div>
      )}
      
      <div className="card-header bg-gradient-to-r from-blue-50 to-white">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
            <span className="mr-2">🏪</span>
            Restaurant Profile
          </h2>
          {!editing && (
            <button
              onClick={() => {
                if (!hasProfilePassword) {
                  toast.error('Please set your profile password before editing profile information');
                  return;
                }
                setEditing(true);
              }}
              data-testid="edit-profile-button"
              className="btn-primary py-3 sm:py-2 text-base sm:text-sm font-semibold"
            >
              ✏️ Edit Profile
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Restaurant Name
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                data-testid="profile-name-input"
                className="form-input"
                maxLength={25}
                required
              />
              <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                {formData.name.length}/25
              </div>
            </div>
            {formData.name.length > 20 && (
              <p className="text-xs text-orange-500 mt-1">
                {25 - formData.name.length} characters remaining
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              data-testid="profile-phone-input"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address" className="form-label">
              Address
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              data-testid="profile-address-input"
              className="form-textarea"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="currency" className="form-label">
              Currency
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              data-testid="profile-currency-select"
              className="form-input"
            >
              {Object.entries(currencies).map(([code, info]) => (
                <option key={code} value={code}>
                  {info.symbol} {code} - {info.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              This currency will be used for all menu prices and customer payments
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="payment_mode" className="form-label">
              Payment Mode
            </label>
            <select
              id="payment_mode"
              name="payment_mode"
              value={formData.payment_mode}
              onChange={handleInputChange}
              data-testid="profile-payment-mode-select"
              className="form-input"
            >
              <option value="manual_only">Manual Only (Pay Later)</option>
              <option 
                value="payment_only" 
                disabled={!hasStripeConfig()}
                title={!hasStripeConfig() ? "Configure Stripe keys first" : ""}
              >
                Payment Only (Card Payment)
              </option>
              <option 
                value="manual_and_payment"
                disabled={!hasStripeConfig()}
                title={!hasStripeConfig() ? "Configure Stripe keys first" : ""}
              >
                Manual & Payment (Both Options)
              </option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.payment_mode === 'manual_only' && 'Customers can only place orders and pay later'}
              {formData.payment_mode === 'payment_only' && 'Customers must pay with card when placing orders'}
              {formData.payment_mode === 'manual_and_payment' && 'Customers can choose to pay now or later'}
              {!hasStripeConfig() && (formData.payment_mode === 'payment_only' || formData.payment_mode === 'manual_and_payment') && (
                <span className="text-amber-600"> (Stripe configuration required for payment modes)</span>
              )}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              data-testid="save-profile-button"
              className="btn-primary w-full sm:w-auto py-3 sm:py-2 text-base font-semibold min-h-[48px] flex items-center justify-center"
            >
              {loading ? (
                <div className="spinner"></div>
              ) : (
                <>
                  <span className="mr-2">💾</span>
                  Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              data-testid="cancel-edit-button"
              className="btn-secondary w-full sm:w-auto py-3 sm:py-2 text-base font-semibold min-h-[48px] flex items-center justify-center"
            >
              <span className="mr-2">✖️</span>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="form-label">Restaurant Name</label>
            <p className="text-gray-900 text-lg font-medium" data-testid="profile-name-display">
              {restaurant?.name}
            </p>
          </div>

          <div>
            <label className="form-label">Email</label>
            <p className="text-gray-600" data-testid="profile-email-display">
              {restaurant?.email}
            </p>
          </div>

          <div>
            <label className="form-label">Phone Number</label>
            <p className="text-gray-600" data-testid="profile-phone-display">
              {restaurant?.phone}
            </p>
          </div>

          <div>
            <label className="form-label">Address</label>
            <p className="text-gray-600" data-testid="profile-address-display">
              {restaurant?.address}
            </p>
          </div>

          <div>
            <label className="form-label">Currency</label>
            <p className="text-gray-600" data-testid="profile-currency-display">
              {currencies[restaurant?.currency]?.symbol} {restaurant?.currency} - {currencies[restaurant?.currency]?.name}
            </p>
            <p className="text-xs text-gray-500">
              All menu prices and payments are processed in this currency
            </p>
          </div>

          <div>
            <label className="form-label">Payment Mode</label>
            <p className="text-gray-600" data-testid="profile-payment-mode-display">
              {restaurant?.payment_mode === 'manual_only' && 'Manual Only (Pay Later)'}
              {restaurant?.payment_mode === 'payment_only' && 'Payment Only (Card Payment)'}
              {restaurant?.payment_mode === 'manual_and_payment' && 'Manual & Payment (Both Options)'}
            </p>
            <p className="text-xs text-gray-500">
              {restaurant?.payment_mode === 'manual_only' && 'Customers can only place orders and pay later'}
              {restaurant?.payment_mode === 'payment_only' && 'Customers must pay with card when placing orders'}
              {restaurant?.payment_mode === 'manual_and_payment' && 'Customers can choose to pay now or later'}
            </p>
          </div>

          <div>
            <label className="form-label">Order Capacity</label>
            <p className="text-gray-600" data-testid="profile-capacity-display">
              {restaurant?.order_capacity} orders remaining
            </p>
          </div>

          <div>
            <label className="form-label">Account Status</label>
            <span 
              className={`px-2 py-1 rounded-full text-sm font-medium ${
                restaurant?.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}
              data-testid="profile-status-display"
            >
              {restaurant?.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div>
            <label className="form-label">Member Since</label>
            <p className="text-gray-600" data-testid="profile-created-display">
              {new Date(restaurant?.created_at).toLocaleDateString()}
            </p>
          </div>

          {/* Stripe Payment Configuration */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Stripe Payment Setup</h3>
              {!editingStripe && (
                <div className="flex items-center space-x-2">
                  {hasStripeConfig() && (
                    <button
                      onClick={() => setShowTestPayment(true)}
                      data-testid="restaurant-test-payment-button"
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="Charge 1 USD to verify your Stripe keys work"
                    >
                      Test Payment (1 USD)
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (!hasProfilePassword) {
                        toast.error('Please set your profile password before editing Stripe configuration');
                        return;
                      }
                      setEditingStripe(true);
                    }}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {stripeConfig.stripe_publishable_key ? 'Edit Configuration' : 'Setup Payments'}
                  </button>
                </div>
              )}
            </div>

            {editingStripe ? (
              <form onSubmit={handleStripeConfigSubmit} className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Configure Stripe for Customer Payments</h4>
                  <p className="text-sm text-yellow-800">
                    Set up your Stripe keys to accept payments from customers ordering from your restaurant.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label htmlFor="stripe_publishable_key" className="form-label">
                      Stripe Publishable Key
                    </label>
                    <input
                      type="text"
                      id="stripe_publishable_key"
                      name="stripe_publishable_key"
                      value={stripeConfig.stripe_publishable_key}
                      onChange={handleStripeConfigChange}
                      placeholder="pk_live_..."
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="stripe_secret_key" className="form-label">
                      Stripe Restricted Key (rk_)
                    </label>
                    <input
                      type="password"
                      id="stripe_secret_key"
                      name="stripe_secret_key"
                      value={stripeConfig.stripe_secret_key}
                      onChange={handleStripeConfigChange}
                      placeholder="rk_live_..."
                      className="form-input"
                      data-testid="restaurant-stripe-secret-key-input"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1" data-testid="restaurant-stripe-secret-key-hint">
                      For security, only a Stripe <strong>Restricted key</strong> (starts with <code>rk_</code>)
                      is accepted. It must have <strong>write</strong> access to
                      <strong> Payment Intents</strong> for card payments to work.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditingStripe(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Save Configuration
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="form-label">Stripe Publishable Key</label>
                    <p className="text-gray-900 font-mono text-sm">
                      {stripeConfig.stripe_publishable_key || 'Not configured'}
                    </p>
                  </div>

                  <div>
                    <label className="form-label">Stripe Restricted Key (rk_)</label>
                    <p className="text-gray-900 font-mono text-sm">
                      {stripeConfig.stripe_secret_key || 'Not configured'}
                    </p>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="restaurant-stripe-help">
                  <h4 className="font-medium text-green-900 mb-2">How to get your Stripe keys</h4>

                  <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-3" data-testid="restaurant-stripe-livemode-note">
                    <p className="text-sm text-amber-900">
                      <strong>Use Live mode keys</strong> so you can accept real customer payments. In your
                      Stripe Dashboard, switch to <strong>Live mode</strong> using the top-right toggle before
                      copying your keys. Live keys start with <code>pk_live_</code> and <code>rk_live_</code>.
                    </p>
                  </div>

                  <p className="text-sm font-semibold text-green-900 mt-1">1. Switch to Live mode</p>
                  <ol className="text-sm text-green-800 space-y-1 list-decimal ml-5">
                    <li>Sign up / log in at <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="underline font-medium">stripe.com</a>.</li>
                    <li>In the Stripe Dashboard, switch to <strong>Live mode</strong> using the top-right toggle.</li>
                  </ol>

                  <p className="text-sm font-semibold text-green-900 mt-3">2. Publishable key (pk_live_)</p>
                  <ol className="text-sm text-green-800 space-y-1 list-decimal ml-5">
                    <li>Open <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Developers → API keys</a>.</li>
                    <li>Copy the <strong>Publishable key</strong> (starts with <code>pk_live_</code>) and paste it above.</li>
                  </ol>

                  <p className="text-sm font-semibold text-green-900 mt-3">3. Restricted key (rk_live_) — required</p>
                  <ol className="text-sm text-green-800 space-y-1 list-decimal ml-5">
                    <li>Go to <a href="https://dashboard.stripe.com/apikeys/create" target="_blank" rel="noopener noreferrer" className="underline font-medium">Create restricted key</a> (make sure you are in Live mode).</li>
                    <li>Give it a name (for example, QR Ordering).</li>
                    <li>Set <strong>Payment Intents</strong> to <strong>Write</strong> (leave everything else as <em>None</em>).</li>
                    <li>Click <strong>Create key</strong>, then copy the key (starts with <code>rk_live_</code>) and paste it above.</li>
                  </ol>
                  <p className="text-xs text-green-800 mt-2 bg-green-100 border border-green-200 rounded-md p-2" data-testid="restaurant-stripe-leastprivilege-note">
                    By granting <strong>only Payment Intents → Write</strong>, this key can be used solely to
                    accept payments into your Stripe account. It <strong>cannot</strong> issue refunds, view
                    your customers or balance, move money, or change any settings — so your account stays fully secure.
                  </p>

                  <p className="text-xs text-green-700 mt-2">
                    Need help? See the <a href="https://docs.stripe.com/keys#limit-access-to-a-restricted-api-key" target="_blank" rel="noopener noreferrer" className="underline font-medium">Stripe restricted keys guide</a>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kitchen Display QR Code Section */}
      {!editing && restaurant && (
        <div className="mt-8">
          <KitchenQRCode 
            restaurantId={restaurant.id} 
            restaurantName={restaurant.name}
          />
        </div>
      )}

      {/* Password Setup Success Modal */}
      {showPasswordSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Password Setup Email Sent</h3>
            <p className="text-gray-600 mb-4">
              We've sent you an email with instructions to set up your profile password. 
              Please check your inbox and follow the link to complete the setup.
            </p>
            <button
              onClick={() => setShowPasswordSetup(false)}
              className="btn-primary w-full"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Password Verification Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Enter Profile Password</h3>
            <p className="text-gray-600 mb-4">
              Please enter your profile password to continue with {passwordAction === 'profile' ? 'profile updates' : 'Stripe configuration changes'}.
            </p>
            <form onSubmit={(e) => {
              e.preventDefault();
              const password = e.target.password.value;
              if (password) {
                handlePasswordSubmit(password);
              }
            }}>
              <div className="mb-4">
                <label htmlFor="password" className="form-label">
                  Profile Password
                </label>
                <input
                  type="password"
                  name="password"
                  className="form-input"
                  placeholder="Enter your profile password"
                  required
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPendingFormData(null);
                    setPendingStripeData(null);
                    setPasswordAction('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <TestPaymentModal
        isOpen={showTestPayment}
        onClose={() => setShowTestPayment(false)}
        apiClient={apiClient}
        createEndpoint="/restaurant/stripe-test-payment"
        title="Test Stripe Payment (1 USD)"
      />

    </div>
  );
};

export default RestaurantProfile;